import type { CostContractDiscountInput, CostContractPriceItem, CostContractPriceItemInput, CostPriceImportItem } from '../types'
import { compareMoneyStrings, fromCents, sumMoneyStrings, toCents } from './contracts'
import { normalizeSearch } from './tree'

export const MAX_PRICE_ITEMS = 2000

/** One editable row of "Upravit rozpis ceny". `key` is the stored id, or a client id for a new row. */
export interface DraftPriceItem {
  key: string
  section: string
  code: string
  name: string
  unit: string
  /** Free text; informational only (spec decision 6). */
  quantity: string
  amount: string | null
  amountValid: boolean
  discountPercent: string | null
  discountValid: boolean
  nodeId: string | null
  sourceBidItemId: string | null
  provisional: boolean
}
export type DiscountMode = 'percent' | 'amount'
export interface DraftDiscount { mode: DiscountMode; value: string | null; valid: boolean }

let sequence = 0
export const newDraftItem = (section = ''): DraftPriceItem => ({
  key: `new-${++sequence}`, section, code: '', name: '', unit: '', quantity: '', amount: null, amountValid: true, discountPercent: null, discountValid: true, nodeId: null, sourceBidItemId: null, provisional: false,
})
export const draftFromPriceItems = (items: CostContractPriceItem[]): DraftPriceItem[] => items.map((i) => ({
  key: i.id, section: i.section ?? '', code: i.code ?? '', name: i.name, unit: i.unit ?? '', quantity: i.quantity === null ? '' : String(i.quantity),
  amount: i.amount, amountValid: true, discountPercent: i.discountPercent, discountValid: true, nodeId: i.nodeId, sourceBidItemId: i.sourceBidItemId, provisional: i.provisional,
}))
/** Freelo #33146759: imported rows join the draft as new, unpaired rows, as a hand-added row would. */
export const draftFromImport = (rows: CostPriceImportItem[]): DraftPriceItem[] => rows.map((r) => ({
  ...newDraftItem(r.section ?? ''), code: r.code ?? '', name: r.name, unit: r.unit ?? '', quantity: r.quantity === null ? '' : String(r.quantity),
  amount: r.amount, discountPercent: r.discountPercent,
}))
export const draftDiscount = (d: { discountPercent: string | null; discountAmount: string | null }): DraftDiscount =>
  d.discountAmount !== null ? { mode: 'amount', value: d.discountAmount, valid: true } : { mode: 'percent', value: d.discountPercent, valid: true }
/** A different mode clears the value: "10" means 10 % or 10 Kč, never both. */
export const switchDiscountMode = (d: DraftDiscount, mode: DiscountMode): DraftDiscount => (d.mode === mode ? d : { mode, value: null, valid: true })

/** "12,5" or "12.5" → 12.5; empty → null; more than three decimals or anything else → undefined (invalid). */
export function parseQuantity(text: string): number | null | undefined {
  const s = text.replace(/[\s\u00a0]/g, '').replace(',', '.')
  if (!s) return null
  return /^\d{1,12}(\.\d{1,3})?$/.test(s) ? Number(s) : undefined
}

const orNull = (s: string) => s.trim() || null
export const toPriceItemInputs = (draft: DraftPriceItem[]): CostContractPriceItemInput[] => draft.map((d) => ({
  section: orNull(d.section), code: orNull(d.code), name: d.name.trim(), unit: orNull(d.unit), quantity: parseQuantity(d.quantity) ?? null,
  amount: d.amount ?? '0.00', discountPercent: d.discountPercent, nodeId: d.nodeId, sourceBidItemId: d.sourceBidItemId, provisional: d.provisional,
}))
export const toDiscountInput = (d: DraftDiscount): CostContractDiscountInput | null =>
  d.value === null ? null : d.mode === 'percent' ? { percent: d.value, amount: null } : { percent: null, amount: d.value }
/** What Save would send; the editor is dirty when this differs from the snapshot taken on opening. */
export const priceSnapshot = (draft: DraftPriceItem[], d: DraftDiscount): string => JSON.stringify([toPriceItemInputs(draft), toDiscountInput(d)])

// ---- Spec §2 in whole cents, the same rounding as the server's priceBreakdown ----

const ZERO = BigInt(0)
const ONE = BigInt(1)
const TWO = BigInt(2)
const BASIS = BigInt(10000) // percent strings have two places: "12.50" → 1250 basis points
/** n / d rounded half up, for n ≥ 0. */
const roundDiv = (n: bigint, d: bigint): bigint => (n * TWO + d) / (d * TWO)
const sum = (values: bigint[]) => values.reduce((a, v) => a + v, ZERO)

/** The server's allocate(): pro rata in whole cents, leftover cents to the largest remainders (ties: earlier index); all-zero weights split equally. */
export function allocateCents(total: bigint, weights: bigint[]): bigint[] {
  if (!weights.length) return []
  const weightSum = sum(weights)
  const ws = weightSum === ZERO ? weights.map(() => ONE) : weights
  const denominator = weightSum === ZERO ? BigInt(weights.length) : weightSum
  const parts = ws.map((w) => (total * w) / denominator)
  let left = total - sum(parts)
  const order = ws.map((w, i) => ({ i, rem: (total * w) % denominator })).sort((a, b) => (a.rem === b.rem ? a.i - b.i : a.rem > b.rem ? -1 : 1))
  for (let k = 0; left > ZERO; k++, left -= ONE) parts[order[k].i] += ONE
  return parts
}

export interface PriceCalc {
  finals: string[]; gross: string; afterItemDiscounts: string; contractDiscount: string; total: string; unmapped: string; unmappedCount: number; discountTooLarge: boolean
}

/** The editor's live totals (spec §2). An empty amount counts as 0; a discount above the sum is flagged, not split. */
export function priceCalc(items: { amount: string | null; discountPercent: string | null; nodeId: string | null }[], discount: { percent: string | null; amount: string | null }): PriceCalc {
  const amounts = items.map((i) => (i.amount === null ? ZERO : toCents(i.amount)))
  const nets = items.map((it, i) => (it.discountPercent === null ? amounts[i] : roundDiv(amounts[i] * (BASIS - toCents(it.discountPercent)), BASIS)))
  const afterItemDiscounts = sum(nets)
  const contractDiscount = discount.amount !== null ? toCents(discount.amount) : discount.percent !== null ? roundDiv(afterItemDiscounts * toCents(discount.percent), BASIS) : ZERO
  const discountTooLarge = contractDiscount > afterItemDiscounts
  const shares = discountTooLarge ? nets.map(() => ZERO) : allocateCents(contractDiscount, nets)
  const finals = nets.map((n, i) => n - shares[i])
  let unmapped = ZERO
  let unmappedCount = 0
  items.forEach((it, i) => {
    if (it.nodeId) return
    unmapped += finals[i]
    unmappedCount++
  })
  return {
    finals: finals.map(fromCents), gross: fromCents(sum(amounts)), afterItemDiscounts: fromCents(afterItemDiscounts), contractDiscount: fromCents(contractDiscount),
    total: fromCents(afterItemDiscounts - contractDiscount), unmapped: fromCents(unmapped), unmappedCount, discountTooLarge,
  }
}

const inRange = (p: string | null) => p === null || (compareMoneyStrings(p, '0') >= 0 && compareMoneyStrings(p, '100') <= 0)

/** Live totals of a draft: invalid amounts and percentages count as empty, and Save stays refused (priceItemsProblem). */
export const draftCalc = (draft: DraftPriceItem[], d: DraftDiscount): PriceCalc => priceCalc(
  draft.map((i) => ({ amount: i.amountValid ? i.amount : null, discountPercent: i.discountValid && inRange(i.discountPercent) ? i.discountPercent : null, nodeId: i.nodeId })),
  d.valid && d.value !== null && (d.mode === 'amount' || inRange(d.value)) ? (d.mode === 'percent' ? { percent: d.value, amount: null } : { percent: null, amount: d.value }) : { percent: null, amount: null },
)

/** Part D spec §2.1: the rows mapped to one budget item are all provisional or none (the server refuses a mix). */
export function hasMixedProvisional(draft: Pick<DraftPriceItem, 'nodeId' | 'provisional'>[]): boolean {
  const flag = new Map<string, boolean>()
  return draft.some((i) => {
    if (!i.nodeId) return false
    if (flag.has(i.nodeId) && flag.get(i.nodeId) !== i.provisional) return true
    flag.set(i.nodeId, i.provisional)
    return false
  })
}

/** The first reason Save is refused, as an i18n key (the server checks the same), or null. */
export function priceItemsProblem(draft: DraftPriceItem[], d: DraftDiscount, calc: PriceCalc): string | null {
  if (draft.length > MAX_PRICE_ITEMS) return 'CostControl_error_too_many_items'
  if (!d.valid || draft.some((i) => !i.amountValid || !i.discountValid || i.amount === null || parseQuantity(i.quantity) === undefined)) return 'CostControl_price_problem_invalid'
  if (draft.some((i) => !i.name.trim())) return 'CostControl_error_item_name_required'
  if (hasMixedProvisional(draft)) return 'CostControl_error_provisional_mixed_item'
  if (draft.some((i) => !inRange(i.discountPercent)) || (d.mode === 'percent' && !inRange(d.value))) return 'CostControl_error_discount_percent_range'
  if (calc.discountTooLarge) return 'CostControl_error_discount_too_large'
  return null
}

/** Bulk "Přiřadit k položce rozpočtu" / "Zrušit přiřazení": only the selected rows change. */
export const assignNode = (draft: DraftPriceItem[], keys: ReadonlySet<string>, nodeId: string | null): DraftPriceItem[] =>
  draft.map((i) => (keys.has(i.key) ? { ...i, nodeId } : i))

/** "Nenapárované" filter and a search on code and name, without diacritics. */
export function visibleItems(draft: DraftPriceItem[], f: { unmappedOnly: boolean; query: string }): DraftPriceItem[] {
  const q = normalizeSearch(f.query)
  return draft.filter((i) => (!f.unmappedOnly || !i.nodeId) && (!q || normalizeSearch(`${i.code} ${i.name}`).includes(q)))
}

/** Keys of the selected rows that are actually visible: hidden rows never take part in a bulk action. */
export const actingKeys = (visible: DraftPriceItem[], selected: ReadonlySet<string>): Set<string> =>
  new Set(visible.filter((i) => selected.has(i.key)).map((i) => i.key))

export interface PriceSection { key: string; section: string | null; items: CostContractPriceItem[]; subtotal: string }
/** Consecutive items of one section, in the stored order, with the sum of their final amounts (spec §8). */
export function priceSections(items: CostContractPriceItem[]): PriceSection[] {
  const out: PriceSection[] = []
  items.forEach((it) => {
    const last = out[out.length - 1]
    if (last && last.section === it.section) last.items.push(it)
    else out.push({ key: it.id, section: it.section, items: [it], subtotal: '0.00' })
  })
  out.forEach((s) => { s.subtotal = sumMoneyStrings(s.items.map((i) => i.finalAmount)) })
  return out
}

/** Unit price for display only (amount / quantity, two places); null without a positive quantity. */
export const unitPrice = (amount: string, quantity: number | null): string | null => (quantity && quantity > 0 ? (Number(amount) / quantity).toFixed(2) : null)
